import React from 'react';
import { Plus, Trash2, Ruler, Paperclip, Square, BookOpen } from 'lucide-react';
import { BasicResourceConfigurationFormProps } from '../../../types';

interface MeasurementObject {
  id: string;
  name: string;
  length: number;
  unit: string;
  color: string;
  shape: 'rectangle' | 'circle' | 'triangle';
}

interface MeasurementUnit {
  id: string;
  name: string;
  icon: string;
  color: string;
  size: number;
}

const iconOptions = [
  { value: 'ruler', label: 'Règle', icon: Ruler },
  { value: 'paperclip', label: 'Trombone', icon: Paperclip },
  { value: 'cube', label: 'Cube', icon: Square },
  { value: 'book', label: 'Livre', icon: BookOpen },
];

const shapeOptions = [
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'circle', label: 'Cercle' },
  { value: 'triangle', label: 'Triangle' },
];

const colorOptions = [
  { value: 'bg-red-400', label: 'Rouge' },
  { value: 'bg-blue-400', label: 'Bleu' },
  { value: 'bg-green-400', label: 'Vert' },
  { value: 'bg-yellow-400', label: 'Jaune' },
  { value: 'bg-purple-400', label: 'Violet' },
  { value: 'bg-pink-400', label: 'Rose' },
  { value: 'bg-gray-400', label: 'Gris' },
  { value: 'bg-orange-400', label: 'Orange' },
];

const themeOptions = [
  { value: 'primary', label: 'Primaire (Bleu)' },
  { value: 'secondary', label: 'Secondaire (Gris)' },
  { value: 'success', label: 'Succès (Vert)' },
  { value: 'warning', label: 'Avertissement (Jaune)' },
];

export const MeasurementConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate,
}) => {
  const objects = config?.objects || resource.objects || [
    { id: '1', name: 'Crayon', length: 5, unit: 'paperclips', color: 'bg-yellow-400', shape: 'rectangle' },
    { id: '2', name: 'Gomme', length: 3, unit: 'cubes', color: 'bg-pink-400', shape: 'rectangle' },
    { id: '3', name: 'Stylo', length: 6, unit: 'paperclips', color: 'bg-blue-400', shape: 'rectangle' },
  ];

  const units = config?.units || resource.units || [
    { id: 'paperclips', name: 'Trombones', icon: 'paperclip', color: 'bg-gray-400', size: 1 },
    { id: 'cubes', name: 'Cubes', icon: 'cube', color: 'bg-red-400', size: 1 },
    { id: 'books', name: 'Livres', icon: 'book', color: 'bg-green-400', size: 1 },
  ];

  const showAnswer = config?.showAnswer ?? resource.showAnswer ?? false;
  const allowInteraction = config?.allowInteraction ?? resource.allowInteraction ?? true;
  const theme = config?.theme ?? resource.theme ?? 'primary';
  const prompt = config?.prompt ?? resource.prompt ?? '';

  const addObject = () => {
    const newObject: MeasurementObject = {
      id: Date.now().toString(),
      name: 'Nouvel objet',
      length: 1,
      unit: 'paperclips',
      color: 'bg-blue-400',
      shape: 'rectangle',
    };
    onUpdate({
      objects: [...objects, newObject]
    });
  };

  const removeObject = (id: string) => {
    const newObjects = objects.filter((obj: any) => obj.id !== id);
    onUpdate({
      objects: newObjects
    });
  };

  const updateObject = (id: string, field: keyof MeasurementObject, value: any) => {
    const newObjects = objects.map((obj: any) =>
      obj.id === id ? { ...obj, [field]: value } : obj
    );
    onUpdate({
      objects: newObjects
    });
  };

  const addUnit = () => {
    const newUnit: MeasurementUnit = {
      id: Date.now().toString(),
      name: 'Nouvelle unité',
      icon: 'ruler',
      color: 'bg-gray-400',
      size: 1,
    };
    onUpdate({
      units: [...units, newUnit]
    });
  };

  const removeUnit = (id: string) => {
    const newUnits = units.filter((unit: any) => unit.id !== id);
    onUpdate({
      units: newUnits
    });
  };

  const updateUnit = (id: string, field: keyof MeasurementUnit, value: any) => {
    const newUnits = units.map((unit: any) =>
      unit.id === id ? { ...unit, [field]: value } : unit
    );
    onUpdate({
      units: newUnits
    });
  };

  return (
    <div className="space-y-6">
      {/* Question */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question ou instruction
        </label>
        <input
          type="text"
          value={prompt}
          onChange={(e) => onUpdate({ prompt: e.target.value })}
          placeholder="Mesurez les objets avec les unités appropriées"
          className="input-field w-full"
        />
      </div>

      {/* Display Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Options d'affichage
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showAnswer}
                onChange={(e) => onUpdate({ showAnswer: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-gray-600">Afficher les réponses</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={allowInteraction}
                onChange={(e) => onUpdate({ allowInteraction: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm text-gray-600">Permettre l'interaction</span>
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Thème
          </label>
          <select
            value={theme}
            onChange={(e) => onUpdate({ theme: e.target.value as any })}
            className="input-field w-full"
          >
            {themeOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Objects */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Objets à mesurer
          </label>
          <button
            type="button"
            onClick={addObject}
            className="btn-secondary flex items-center space-x-1 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un objet</span>
          </button>
        </div>

        <div className="space-y-4">
          {objects.map((object: any, index: number) => (
            <div key={object.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Objet {index + 1}
                </h4>
                <button
                  type="button"
                  onClick={() => removeObject(object.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={object.name}
                    onChange={(e) => updateObject(object.id, 'name', e.target.value)}
                    placeholder="Nom de l'objet"
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Longueur
                  </label>
                  <input
                    type="number"
                    value={object.length}
                    onChange={(e) => updateObject(object.id, 'length', parseInt(e.target.value) || 1)}
                    min="1"
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Unité
                  </label>
                  <input
                    type="text"
                    value={object.unit}
                    onChange={(e) => updateObject(object.id, 'unit', e.target.value)}
                    placeholder="Unité de base"
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Forme
                  </label>
                  <select
                    value={object.shape}
                    onChange={(e) => updateObject(object.id, 'shape', e.target.value)}
                    className="input-field w-full"
                  >
                    {shapeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Couleur
                  </label>
                  <select
                    value={object.color}
                    onChange={(e) => updateObject(object.id, 'color', e.target.value)}
                    className="input-field w-full"
                  >
                    {colorOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Units */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="block text-sm font-medium text-gray-700">
            Unités de mesure
          </label>
          <button
            type="button"
            onClick={addUnit}
            className="btn-secondary flex items-center space-x-1 text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter une unité</span>
          </button>
        </div>

        <div className="space-y-4">
          {units.map((unit: any, index: number) => (
            <div key={unit.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Unité {index + 1}
                </h4>
                <button
                  type="button"
                  onClick={() => removeUnit(unit.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={unit.name}
                    onChange={(e) => updateUnit(unit.id, 'name', e.target.value)}
                    placeholder="Nom de l'unité"
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Icône
                  </label>
                  <select
                    value={unit.icon}
                    onChange={(e) => updateUnit(unit.id, 'icon', e.target.value)}
                    className="input-field w-full"
                  >
                    {iconOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Taille
                  </label>
                  <input
                    type="number"
                    value={unit.size}
                    onChange={(e) => updateUnit(unit.id, 'size', parseFloat(e.target.value) || 1)}
                    min="0.1"
                    step="0.1"
                    className="input-field w-full"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Couleur
                  </label>
                  <select
                    value={unit.color}
                    onChange={(e) => updateUnit(unit.id, 'color', e.target.value)}
                    className="input-field w-full"
                  >
                    {colorOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      {objects.length > 0 && units.length > 0 && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Aperçu</h4>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-2">
              {objects.slice(0, 2).map((object: any) => (
                <div key={object.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{object.name}:</span>
                  <div className="flex items-center space-x-2">
                    {units.slice(0, 2).map((unit: any) => {
                      const measurement = Math.round(object.length / unit.size);
                      return (
                        <div key={unit.id} className="flex items-center space-x-1">
                          <span className="text-xs text-gray-500">{unit.name}:</span>
                          <span className="font-medium">{measurement}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {objects.length > 2 && (
                <div className="text-xs text-gray-500">
                  ... et {objects.length - 2} autres objets
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
